import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { api, call } from '../../api/client';
import { useAction, useFamily, useInvitations, useMembers } from '../../api/queries';
import { useHousehold, useSession } from '../../api/session';
import { Avatar, Badge, Button, Card, ErrorText, Field, Input, PageHeader, Select, Spinner } from '../../components/ui';
import { formatDay } from '../../lib/format';
import { ADULT_LABELS } from '../onboarding/OnboardingPages';

export function FamilySettingsPage() {
  const { familyId, isOwner, memberId } = useHousehold();
  const { reloadMe } = useSession();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const family = useFamily().data;
  const members = useMembers().data ?? [];
  const invitations = useInvitations().data ?? [];
  const path = { params: { path: { familyId } } };
  const [confirmName, setConfirmName] = useState('');

  const update = useAction(
    (f: FormData) =>
      call(api.PATCH('/api/v1/families/{familyId}', { ...path, body: { name: String(f.get('name')), weekStartsOn: Number(f.get('weekStartsOn')) === 7 ? 7 : 1, childCompletionMode: f.get('mode') as 'DIRECT' } })),
    { success: 'Saved' },
  );
  // Invitations are shared as links (ADR-0021): the link is shown once, right after it's created or re-issued.
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const invite = useAction(
    async (f: FormData) => {
      const email = String(f.get('email') ?? '').trim();
      const created = await call(
        api.POST('/api/v1/families/{familyId}/invitations', { ...path, body: { ...(email ? { email } : {}), ...(f.get('label') ? { relationshipLabelCode: f.get('label') as 'PARTNER' } : {}) } }),
      );
      setInviteUrl(created.inviteUrl ?? null);
      return created;
    },
  );
  const invitationAction = useAction(async ({ id, action }: { id: string; action: 'resend' | 'revoke' }) => {
    const result = await call(
      api.POST(action === 'resend' ? '/api/v1/families/{familyId}/invitations/{invitationId}/resend' : '/api/v1/families/{familyId}/invitations/{invitationId}/revoke', {
        params: { path: { familyId, invitationId: id } },
      }),
    );
    setInviteUrl(action === 'resend' ? (result.inviteUrl ?? null) : null);
    return result;
  });
  const leave = useAction(() => call(api.POST('/api/v1/families/{familyId}/members/me/leave', path)));
  const scheduleDeletion = useAction(() => call(api.POST('/api/v1/families/{familyId}/deletion', { ...path, body: { confirmName } })), { success: 'Deletion scheduled' });
  const cancelDeletion = useAction(() => call(api.DELETE('/api/v1/families/{familyId}/deletion', path)), { success: 'Deletion cancelled' });

  if (!family) return <Spinner />;
  const adults = members.filter((m) => m.role === 'ADULT');
  const pendingInvites = invitations.filter((i) => i.status === 'PENDING');
  const soleAdult = adults.length === 1;

  return (
    <>
      <PageHeader title="Family Settings" />
      <div className="space-y-6">
        <Card>
          <h2 className="mb-3 font-medium">Household</h2>
          <form className="space-y-3" onSubmit={(e: FormEvent<HTMLFormElement>) => { e.preventDefault(); update.mutate(new FormData(e.currentTarget)); }}>
            <Field label="Name">{(id) => <Input id={id} name="name" defaultValue={family.name} maxLength={80} required />}</Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Week starts on">{(id) => <Select id={id} name="weekStartsOn" defaultValue={String(family.weekStartsOn)}><option value="1">Monday</option><option value="7">Sunday</option></Select>}</Field>
              <Field label="Children's chores" hint="Verification by a parent is coming later.">
                {(id) => <Select id={id} name="mode" defaultValue={family.childCompletionMode}><option value="DIRECT">Done when marked done</option><option value="REQUIRE_VERIFICATION">Parent checks first</option></Select>}
              </Field>
            </div>
            <p className="text-xs text-[var(--muted)]">Time zone: {family.timezone}</p>
            <Button type="submit" variant="secondary" busy={update.isPending}>Save</Button>
          </form>
        </Card>

        <Card>
          <h2 className="mb-3 font-medium">Adults</h2>
          <ul className="space-y-2">
            {adults.map((m) => (
              <li key={m.id} className="flex items-center gap-3 text-sm">
                <Avatar name={m.displayName} />
                <span className="flex-1">{m.displayName}{m.isMe ? ' (you)' : ''}</span>
                {m.relationshipLabel.code ? <Badge>{m.relationshipLabel.custom ?? m.relationshipLabel.code.toLowerCase()}</Badge> : null}
                {m.isOwner ? <Badge tone="accent">Owner</Badge> : null}
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-[var(--muted)]">Both adults have the same say over household work. The owner only manages invitations and household deletion.</p>
          {isOwner && adults.length + pendingInvites.length < family.maxAdults ? (
            <form className="mt-4 space-y-3 border-t border-[var(--border)] pt-4" onSubmit={(e: FormEvent<HTMLFormElement>) => { e.preventDefault(); const form = e.currentTarget; invite.mutate(new FormData(form), { onSuccess: () => form.reset() }); }}>
              <h3 className="text-sm font-medium">Invite your partner</h3>
              <p className="text-xs text-[var(--muted)]">You'll get a link to send them however you like: text, WhatsApp or your own email. Adding their email (optional) means only they can use it.</p>
              <div className="grid grid-cols-[1fr_auto] gap-2">
                <Input name="email" type="email" placeholder="partner@example.com (optional)" aria-label="Partner's email (optional)" />
                <Select name="label" aria-label="Label" defaultValue="">{ADULT_LABELS.filter(([v]) => v !== 'OTHER').map(([v, l]) => <option key={v} value={v}>{l}</option>)}</Select>
              </div>
              <ErrorText error={invite.error} />
              <Button type="submit" busy={invite.isPending}>Create invite link</Button>
            </form>
          ) : null}
          {inviteUrl ? <InviteLink url={inviteUrl} familyName={family.name} onDone={() => setInviteUrl(null)} /> : null}
          {pendingInvites.length ? (
            <ul className="mt-4 space-y-2 border-t border-[var(--border)] pt-4 text-sm">
              {pendingInvites.map((i) => (
                <li key={i.id} className="flex flex-wrap items-center gap-2">
                  <span className="flex-1">Invited {i.email ?? 'by link'} · expires {formatDay(String(i.expiresAt).slice(0, 10))}</span>
                  {isOwner ? (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => invitationAction.mutate({ id: i.id, action: 'resend' })}>New link</Button>
                      <Button size="sm" variant="ghost" onClick={() => invitationAction.mutate({ id: i.id, action: 'revoke' })}>Revoke</Button>
                    </>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
        </Card>

        <Card>
          <h2 className="mb-2 font-medium">Leave or delete</h2>
          {family.deletionScheduledFor ? (
            <div className="space-y-2 text-sm">
              <p className="text-red-700 dark:text-red-300">This household will be permanently deleted on {new Date(family.deletionScheduledFor).toLocaleString()}.</p>
              <Button variant="secondary" busy={cancelDeletion.isPending} onClick={() => cancelDeletion.mutate(undefined)}>Cancel deletion</Button>
            </div>
          ) : (
            <div className="space-y-4 text-sm">
              {!soleAdult ? (
                <div>
                  <p className="mb-2 text-[var(--muted)]">Your open tasks go back to the House Pool.{isOwner ? ' Ownership passes to the other adult.' : ''}</p>
                  <Button
                    variant="secondary"
                    busy={leave.isPending}
                    onClick={() => {
                      if (!confirm('Leave this household?')) return;
                      leave.mutate(undefined, { onSuccess: async () => { qc.clear(); await reloadMe(); await navigate({ to: '/onboarding' }); } });
                    }}
                  >
                    Leave household
                  </Button>
                </div>
              ) : null}
              {isOwner ? (
                <div>
                  <p className="mb-2 text-[var(--muted)]">Deletes all tasks, children and history after 7 days. {soleAdult ? '' : 'The other adult is emailed and can cancel.'}</p>
                  <div className="flex gap-2">
                    <Input value={confirmName} onChange={(e) => setConfirmName(e.target.value)} placeholder={`Type “${family.name}” to confirm`} aria-label="Confirm household name" />
                    <Button variant="danger" disabled={confirmName !== family.name} busy={scheduleDeletion.isPending} onClick={() => scheduleDeletion.mutate(undefined)}>Delete</Button>
                  </div>
                </div>
              ) : null}
            </div>
          )}
          <ErrorText error={leave.error ?? scheduleDeletion.error} />
        </Card>
        <p className="text-xs text-[var(--muted)]">Member ID: {memberId}</p>
      </div>
    </>
  );
}

/** The link to send to the partner: copy it, or use the device's share sheet. Shown once (only its hash is stored). */
function InviteLink({ url, familyName, onDone }: { url: string; familyName: string; onDone(): void }) {
  const [copied, setCopied] = useState(false);
  const text = `Join our household "${familyName}" on FamilyChores:`;
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  return (
    <div className="mt-4 space-y-2 rounded-xl border border-[var(--accent)] bg-[var(--accent-soft)] p-3 text-sm">
      <p className="font-medium">Send this link to your partner</p>
      <code className="block break-all rounded-lg bg-[var(--surface)] px-2 py-1.5 font-mono text-xs" aria-label="Invite link">{url}</code>
      <p className="text-xs text-[var(--muted)]">It works once and expires in 7 days. Anyone with it can join unless you added their email, so send it only to them.</p>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={() => void navigator.clipboard?.writeText(url).then(() => setCopied(true))}>{copied ? 'Copied' : 'Copy link'}</Button>
        {canShare ? <Button size="sm" variant="secondary" onClick={() => void navigator.share({ title: 'FamilyChores invitation', text, url }).catch(() => undefined)}>Share…</Button> : null}
        <Button size="sm" variant="ghost" onClick={onDone}>Done</Button>
      </div>
    </div>
  );
}
